/**
 * Stable normalization for `buildingInstanceId` produced by the classifier.
 *
 * The classifier is an LLM and emits inconsistent variants for the same
 * physical building across files (e.g. `hus-1`, `Hus_1`, `sigbardiorden hus 1`,
 * `sigbardiorden_hus_1`). Joined blueprint extraction groups by exact key
 * equality, so without normalization a multi-file building gets split into
 * many single-file groups and never benefits from cross-file analysis.
 *
 * Applied at classifier ingestion (write path) so every downstream consumer —
 * joined extraction, merger payloads, admin UI — sees the same canonical key.
 */

const NON_ASCII_ALPHANUM_RE = /[^a-z0-9]+/g;
const COMBINING_MARKS_RE = /[\u0300-\u036f]/g;
const TRIM_UNDERSCORES_RE = /^_+|_+$/g;
/**
 * Insert `_` at letter↔digit boundaries so `hus1` collapses with `hus_1` /
 * `hus-1`. Common in classifier output where the LLM occasionally drops the
 * separator before the building number.
 */
const LETTER_DIGIT_BOUNDARY_RE = /([a-z])(\d)|(\d)([a-z])/g;

/**
 * Returns a canonical lowercase ASCII slug for a building instance id.
 * Returns `null` when the input is null/undefined/empty after trim — keeps
 * "absent" distinguishable from "present but empty".
 */
export const normalizeBuildingInstanceKey = (
  raw: string | null | undefined,
): string | null => {
  if (raw == null) return null;
  const trimmed = raw.trim();
  if (trimmed.length === 0) return null;
  const slug = trimmed
    .toLowerCase()
    .normalize('NFKD')
    .replace(COMBINING_MARKS_RE, '')
    .replace(LETTER_DIGIT_BOUNDARY_RE, (_m, a, b, c, d) =>
      a != null ? `${a}_${b}` : `${c}_${d}`,
    )
    .replace(NON_ASCII_ALPHANUM_RE, '_')
    .replace(TRIM_UNDERSCORES_RE, '');
  return slug.length > 0 ? slug : null;
};

/**
 * Domain tokens for building references. Longer tokens first so "huskropp"
 * is not eaten by "hus" and "kvarter" is not eaten by "kv".
 *
 * Generic Swedish/English words for a building or a named group of buildings
 * (a kvarter, an etapp). Not project names, not typology indexes.
 */
const BUILDING_TOKENS = [
  'huskropp',
  'byggnad',
  'building',
  'kvarter',
  'lamell',
  'etapp',
  'block',
  'hus',
  'kv',
] as const;

type BuildingToken = (typeof BUILDING_TOKENS)[number];

/** Token → canonical slug prefix used in `<prefix>_<id>` keys. */
const CANONICAL_PREFIX: Record<BuildingToken, string> = {
  huskropp: 'huskropp',
  byggnad: 'hus',
  building: 'hus',
  kvarter: 'kv',
  lamell: 'lamell',
  etapp: 'etapp',
  block: 'block',
  hus: 'hus',
  kv: 'kv',
};

/** Display word used when we synthesize a missing label. */
const DISPLAY_TOKEN: Record<BuildingToken, string> = {
  huskropp: 'Huskropp',
  byggnad: 'Hus',
  building: 'Hus',
  kvarter: 'Kvarter',
  lamell: 'Lamell',
  etapp: 'Etapp',
  block: 'Block',
  hus: 'Hus',
  kv: 'Kv',
};

const ID_PART = String.raw`[a-z]|\d+`;
/** Separators between ids in one reference: "1-3", "5 och 6", "A, B & C". */
const ID_LIST_TAIL = String.raw`(?:\s*[-–—]\s*|\s+och\s+|\s*[&,/]\s*)(?:${ID_PART})`;

// `_` and `-` between the token and the id are separators (`hus_2`, `kv-1`).
// A hyphen after the id is only a building list (`1-3`, `A-C`). A leftover
// hyphen (`KV39-A2-40-1-100`) is a drawing number, not a building.
const BUILDING_REFS_RE = new RegExp(
  `(?:^|[^a-z0-9])(${BUILDING_TOKENS.join('|')})[\\s_-]*(${ID_PART})((?:${ID_LIST_TAIL})*)(?=$|[^a-z0-9\\-–—])`,
  'gi',
);

/** Inclusive range only when both ids are the same kind and the span is a building list, not a page range. */
const MAX_BUILDING_RANGE = 12;

const isBuildingToken = (value: string): value is BuildingToken =>
  (BUILDING_TOKENS as readonly string[]).includes(value);

/**
 * Canonical reference extracted from a free-form building label or id.
 * Uses domain knowledge about Swedish/English building tokens (Hus, Byggnad,
 * Building, Block) so all variants of "Hus 1" — including project-name-
 * prefixed ids like `kv_sigbardiorden_hus_1` — collapse to one stable
 * `{key:'hus_1', label:'Hus 1'}` pair.
 */
export type BuildingReference = {
  /** Canonical slug, e.g. `hus_1`, `hus_a`, `block_c`. */
  key: string;
  /** Display label, e.g. `Hus 1`, `Hus A`, `Block C`. */
  label: string;
};

const displayId = (idPart: string): string =>
  idPart.toUpperCase() === idPart.toLowerCase() ? idPart : idPart.toUpperCase();

const referenceFor = (
  token: BuildingToken,
  idPart: string,
): BuildingReference => {
  const id = idPart.toLowerCase();
  return {
    key: `${CANONICAL_PREFIX[token]}_${id}`,
    label: `${DISPLAY_TOKEN[token]} ${displayId(id)}`,
  };
};

const expandListedIds = (first: string, tail: string): string[] => {
  const ids = [first.toLowerCase()];
  const piece = new RegExp(
    String.raw`(\s*[-–—]\s*|\s+och\s+|\s*[&,/]\s*)(${ID_PART})`,
    'gi',
  );
  for (const match of tail.matchAll(piece)) {
    const sep = match[1] ?? '';
    const next = match[2]?.toLowerCase();
    if (next == null || next === '') continue;
    const prev = ids[ids.length - 1];
    const dashed = /^[\s]*[-–—]/.test(sep);
    if (dashed && prev != null && /^\d+$/.test(prev) && /^\d+$/.test(next)) {
      const from = Number(prev);
      const to = Number(next);
      if (to >= from && to - from <= MAX_BUILDING_RANGE) {
        for (let n = from + 1; n <= to; n += 1) ids.push(String(n));
        continue;
      }
    }
    if (
      dashed &&
      prev != null &&
      /^[a-z]$/.test(prev) &&
      /^[a-z]$/.test(next)
    ) {
      const from = prev.charCodeAt(0);
      const to = next.charCodeAt(0);
      if (to >= from && to - from <= MAX_BUILDING_RANGE) {
        for (let n = from + 1; n <= to; n += 1) {
          ids.push(String.fromCharCode(n));
        }
        continue;
      }
    }
    ids.push(next);
  }
  return [...new Set(ids)];
};

/**
 * Every building named in free text, including ranges ("Hus 1-3", "Kv 1–6")
 * and lists ("Kvarter 5 och 6", "Hus A, B"). Empty when the text names none.
 */
export const extractBuildingReferences = (
  text: string | null | undefined,
): BuildingReference[] => {
  if (text == null || text.trim() === '') return [];
  BUILDING_REFS_RE.lastIndex = 0;
  const out: BuildingReference[] = [];
  const seen = new Set<string>();
  for (const match of text.matchAll(BUILDING_REFS_RE)) {
    const tokenRaw = match[1]?.toLowerCase();
    const first = match[2];
    if (tokenRaw == null || first == null || !isBuildingToken(tokenRaw)) {
      continue;
    }
    const end = (match.index ?? 0) + match[0].length;
    const gluedToId = new RegExp(`^${tokenRaw}${first}$`, 'i').test(
      match[0].replace(/^[^a-z0-9]+/i, ''),
    );
    if (gluedToId && /^\s*[-–—]/.test(text.slice(end))) continue;
    for (const idPart of expandListedIds(first, match[3] ?? '')) {
      const ref = referenceFor(tokenRaw, idPart);
      if (seen.has(ref.key)) continue;
      seen.add(ref.key);
      out.push(ref);
    }
  }
  return out;
};

/**
 * Extract a {@link BuildingReference} from a label and/or id. Label wins when
 * both match because labels echo the in-document name verbatim. Returns the
 * first building when the text names several. Returns null when no recognised
 * token is found — caller falls back to {@link normalizeBuildingInstanceKey}.
 */
export const extractBuildingReference = (
  label: string | null | undefined,
  id: string | null | undefined,
): BuildingReference | null => {
  for (const candidate of [label, id]) {
    const refs = extractBuildingReferences(candidate);
    const first = refs[0];
    if (first !== undefined) return first;
  }
  return null;
};

/**
 * One slug for a building key, whatever step wrote it. A recognised token
 * wins (`kv1` and `Kv 1` → `kv_1`); otherwise the same slug as
 * {@link normalizeBuildingInstanceKey}.
 */
export const canonicalBuildingInstanceKey = (
  raw: string | null | undefined,
): string | null => {
  const ref = extractBuildingReference(null, raw);
  if (ref !== null) return ref.key;
  return normalizeBuildingInstanceKey(raw);
};

export type KnownBuildingIdentity = {
  key: string;
  label: string | null;
};

/**
 * Buildings already named in the batch: classifier ids and labels, merger
 * keys and labels. Zone text is attributed only when it names one of these.
 */
export const knownBuildingIdentities = (
  rows: readonly {
    key?: string | null;
    label?: string | null;
  }[],
): KnownBuildingIdentity[] => {
  const byKey = new Map<string, KnownBuildingIdentity>();
  const add = (
    key: string | null | undefined,
    label: string | null | undefined,
  ) => {
    const canonical = canonicalBuildingInstanceKey(key);
    const fromLabel = extractBuildingReference(label, null);
    const resolved = canonical ?? fromLabel?.key ?? null;
    if (resolved == null) return;
    const existing = byKey.get(resolved);
    const nextLabel =
      label?.trim() || fromLabel?.label || existing?.label || null;
    byKey.set(resolved, { key: resolved, label: nextLabel });
  };
  for (const row of rows) add(row.key, row.label);
  return [...byKey.values()];
};

const uniqueKeys = (keys: readonly string[]): string[] => [...new Set(keys)];

/**
 * Buildings a drawing belongs to. When the label names one or more known
 * buildings, those are the attachment — a shared sheet stays one drawing
 * tagged to each named building, and a neighbor id that the label does not
 * name is dropped. Otherwise the ids that match `known` are kept. An id
 * that is not in `known` (a project-name slug) is dropped.
 */
export const resolveDrawingBuildingKeys = (
  ids: readonly (string | null | undefined)[],
  label: string | null | undefined,
  known: readonly KnownBuildingIdentity[],
): string[] => {
  if (known.length === 0) return [];
  const knownKeys = new Set(known.map((building) => building.key));
  const named = uniqueKeys(
    extractBuildingReferences(label)
      .map((ref) => ref.key)
      .filter((key) => knownKeys.has(key)),
  );
  if (named.length > 0) return named;
  return uniqueKeys(
    [...ids, label].flatMap((value) => {
      const key = canonicalBuildingInstanceKey(value);
      return key != null && knownKeys.has(key) ? [key] : [];
    }),
  );
};

/**
 * Buildings a classifier segment covers. An explicit id list is kept.
 * A label, excerpt, or note that names several buildings (a range or a list)
 * is unioned in, so one sheet is not reduced to a single id.
 */
export const buildingInstanceIdsFromClassification = (data: {
  buildingInstanceId?: string | null;
  buildingInstanceIds?: readonly (string | null)[] | null;
  buildingLabel?: string | null;
  notes?: string | null;
  evidenceExcerpt?: string | null;
}): string[] => {
  const stated = uniqueKeys(
    [
      data.buildingInstanceId ?? null,
      ...(data.buildingInstanceIds ?? []),
    ].flatMap((id) => {
      const key = canonicalBuildingInstanceKey(id);
      return key == null ? [] : [key];
    }),
  );
  const fromText = [
    data.buildingLabel,
    data.evidenceExcerpt,
    data.notes,
  ].flatMap((text) => {
    const refs = extractBuildingReferences(text);
    return refs.length > 1 ? refs.map((ref) => ref.key) : [];
  });
  return uniqueKeys([...stated, ...fromText]);
};
